import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomerTable } from './Customers/components/CustomerTable';
import { CustomerFormDrawer } from './Customers/components/CustomerFormDrawer';
import { useCustomers, Customer } from '../hooks/useCustomers';

const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const { customers, error, createCustomer, updateCustomer, deleteCustomer } = useCustomers();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);

  const handleCreateCustomer = () => {
    setEditingCustomer(null);
    setIsDrawerOpen(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setIsDrawerOpen(true);
  };

  const handleViewCustomer = (customer: Customer) => {
    navigate(`/customers/${customer.id}`);
  };

  const handleSaveCustomer = async (customerData: Partial<Customer>) => {
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, customerData);
      } else {
        await createCustomer(customerData);
      }
      setIsDrawerOpen(false);
      setEditingCustomer(null);
    } catch (error) {
      console.error('Error saving customer:', error);
    }
  };

  // Accepts a Customer object, extracts the ID
  const handleDeleteCustomer = async (customer: Customer) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await deleteCustomer(customer.id);
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingCustomer(null);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error loading customers
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {typeof error === 'string' ? error : (error?.message || 'Failed to load customers')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white">Customers</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage your customer relationships and contact information
          </p>
        </div>
        <button
          onClick={handleCreateCustomer}
          className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white hover:bg-sedra-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
        >
          Add Customer
        </button>
      </div>

      {/* Customer Table */}
      <CustomerTable
        customers={customers}
        loading={false} // isLoading is removed, so set to false
        onEdit={handleEditCustomer}
        onView={handleViewCustomer}
        onDelete={handleDeleteCustomer}
      />

      {/* Customer Form Drawer */}
      <CustomerFormDrawer
        isOpen={isDrawerOpen}
        customer={editingCustomer}
        onSave={handleSaveCustomer}
        onClose={handleCloseDrawer}
        onDelete={(c) => handleDeleteCustomer(c)}
      />
    </div>
  );
};

export default CustomersPage; 